import type { components } from '@/api/schema'
import type { POCommunicationThread } from '@/features/communications/types'

export type CustomerThread = components['schemas']['CustomerCommunicationThreadDetailRead']
export type CustomerAction = CustomerThread['actions'][number]
/** Who the customer is, for the page title: the person, else their Shopify name or address. */
export function customerDisplayName(thread: CustomerThread): string {
  return (
    thread.recipient_name?.trim() ||
    thread.shopify_customer?.display_name?.trim() ||
    thread.recipient_email ||
    thread.recipient_phone ||
    'Customer'
  )
}

/**
 * The customer thread in the PO thread shape the shared conversation components read. Customer
 * threads have no thread CC, check-ins or follow-up reason.
 */
export function toConversationThread(thread: CustomerThread): POCommunicationThread {
  return {
    id: thread.id,
    uuid: thread.uuid,
    entity_type: thread.entity_type,
    entity_id: thread.entity_id,
    purchase_order_id: thread.purchase_order_id ?? null,
    subject: thread.subject,
    status: thread.status,
    graph_conversation_id: thread.graph_conversation_id,
    recipient_email: thread.recipient_email ?? '',
    recipient_name: thread.recipient_name,
    cc_emails: null,
    last_activity_at: thread.last_activity_at,
    created_at: thread.created_at,
    updated_at: thread.updated_at,
    follow_up_at: thread.follow_up_at,
    follow_up_count: thread.follow_up_count,
    max_follow_ups: thread.max_follow_ups,
    notification_type: thread.notification_type,
    messages: thread.messages,
    analysis: thread.analysis,
    follow_up_reason: null,
    upcoming_check_ins: [],
  }
}

type PurchaseOrderAction = NonNullable<
  components['schemas']['PurchaseOrderDetailRead']['actions']
>[number]

/**
 * A customer action item as the PO action cards and email views read one (USA-1328): the same
 * fields a PO action has, with the customer thread's ids. In progress counts as open.
 */
export function toPurchaseOrderAction(action: CustomerAction): PurchaseOrderAction {
  const detail = action.detail
  return {
    id: action.id,
    uuid: action.uuid,
    purchase_order_id: 0,
    line_item_id: null,
    communication_message_id: action.message_id,
    action_type: action.action_type,
    status: action.status === 'in_progress' ? 'open' : action.status,
    summary: action.summary,
    details: action.details,
    structured_data: detail?.structured_data ?? null,
    expected_date: null,
    charge_amount: null,
    assigned_role: detail?.assigned_role ?? 'ops',
    created_by: detail?.created_by ?? null,
    resolved_by: action.resolved_by,
    resolved_at: action.resolved_at,
    resolution_type: detail?.resolution_type ?? null,
    resolution_data: detail?.resolution_data ?? null,
    communicated_at: null,
    customer_approval_required: false,
    customer_approval_status: null,
    customer_communication_thread_id: action.thread_id,
    shipping_communication_thread_id: null,
    customer_draft_subject: null,
    customer_draft_email: null,
    draft_response: detail?.draft_response ?? null,
    customer_decision: null,
    customer_decision_details: null,
    customer_decision_at: null,
    created_at: action.created_at,
    updated_at: action.updated_at,
    card: action.card ?? null,
    progress: action.progress ?? [],
  }
}
